import React, { useState, useEffect } from "react";
import classnames from "classnames";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Badge,
  Table
} from "reactstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GetDashboardData, GetMyStoreOrders } from "../../requests";
import { subscribeToOrderChanges } from "../../helpers/firebaseRealtime";
import ReactApexChart from "react-apexcharts";

// Orders still "in transition" — same definition Orders/index.jsx uses for
// its "Live Orders" tab, kept in sync so the dashboard widget and the full
// list agree on what counts as "live".
const LIVE_ORDER_TERMINAL_STATUSES = ["delivered", "rejected", "cancelled", "canceled"];

const Dashboard = () => {
  document.title = "Dashboard | Susipizza";
  
  const role = useSelector((state) => state.login.role);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [dashData, setDashData] = useState(null);
  const [liveOrders, setLiveOrders] = useState([]);

  // `silent` skips the blocking spinner — used for background refreshes
  // (RTDB live signal + polling fallback) so they don't flash an overlay.
  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await GetDashboardData(token);
      setDashData(response?.data?.data || response?.data || response || {});
    } catch (err) {
      console.error("Error loading dashboard metrics:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Store admin's own store-scoped orders that haven't reached a terminal
  // status yet — the "what still needs my attention" list, separate from
  // dashData's aggregate counts (this needs actual order records, not just
  // numbers, so it can be clicked into).
  const fetchLiveOrders = async () => {
    if (role !== "admin") return;
    try {
      const token = localStorage.getItem("token");
      const resp = await GetMyStoreOrders(token);
      const all = resp.data || [];
      setLiveOrders(
        all.filter((o) => !LIVE_ORDER_TERMINAL_STATUSES.includes((o.status || "").toLowerCase()))
      );
    } catch (err) {
      console.error("Error loading live orders:", err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchLiveOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the dashboard current as orders are placed / statuses change, off
  // the same RTDB signal the Orders page listens to (StatusBroadcastService
  // mirrors every creation + transition there).
  //
  // Two prior bugs fixed here:
  //  1. This was gated `if (role !== "admin") return`, so a SUPERADMIN got
  //     no live updates at all — exactly the "doesn't update without a
  //     refresh" report.
  //  2. It only re-ran fetchLiveOrders(), but the Kitchen Queue widget reads
  //     dashData.kitchenQueue, which only fetchData() populates — so the
  //     kitchen queue never refreshed from the live signal even for a store
  //     admin. Both fetches now run (fetchLiveOrders self-guards to admin).
  useEffect(() => {
    let debounceTimer = null;
    const unsubscribe = subscribeToOrderChanges(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        fetchData({ silent: true });
        fetchLiveOrders();
      }, 1200);
    });
    return () => {
      clearTimeout(debounceTimer);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Safety-net polling behind the RTDB realtime subscription above — keeps
  // the Kitchen Queue / metrics fresh even if live updates are unavailable
  // (RTDB rules, connection). Silent, so it never flashes the spinner.
  useEffect(() => {
    const id = setInterval(() => {
      fetchData({ silent: true });
      fetchLiveOrders();
    }, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const handleRefresh = () => {
    fetchData();
    fetchLiveOrders();
  };

  const handleActivityClick = (statusFilter) => {
    // Navigate to Orders page and pass the active status filter
    navigate("/Orders", { state: { statusFilter } });
  };

  // --- Graph Config (ApexCharts) ---
  const hourlyData = dashData?.salesPerHour || Array(24).fill(0);
  // With an all-zero series (no sales yet today), ApexCharts' auto-scaling
  // picks an arbitrary tiny max (e.g. ₹2) instead of a sensible one — forcing
  // a fixed max only for that empty case keeps real data auto-scaling as before.
  const maxHourlySale = Math.max(...hourlyData, 0);
  const chartSeries = [
    {
      name: "Sales (₹)",
      data: hourlyData,
    },
  ];
  const chartOptions = {
    chart: {
      toolbar: { show: false },
      type: "bar",
      fontFamily: "Outfit, Inter, sans-serif"
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "60%",
        colors: {
          ranges: [{
            from: 0,
            to: 1000000,
            color: "#f4632a" // Custom branding color
          }]
        }
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: [
        "12AM", "1AM", "2AM", "3AM", "4AM", "5AM", "6AM", "7AM", "8AM", "9AM", "10AM", "11AM",
        "12PM", "1PM", "2PM", "3PM", "4PM", "5PM", "6PM", "7PM", "8PM", "9PM", "10PM", "11PM"
      ],
      labels: {
        style: { colors: "#6b7280", fontSize: "10px" }
      }
    },
    yaxis: {
      min: 0,
      max: maxHourlySale > 0 ? undefined : 100,
      labels: {
        formatter: (val) => `₹${Math.round(val).toLocaleString()}`,
        style: { colors: "#6b7280" }
      }
    },
    grid: {
      borderColor: "#f3f4f6",
      strokeDashArray: 4
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: (val) => `₹${val.toLocaleString()}`
      }
    }
  };

  // Helper to format currency
  const formatCurrency = (val) => {
    return `₹${(val || 0).toLocaleString()}`;
  };

  const getStatusColor = (status) => {
    const s = status ? status.toLowerCase() : "";
    if (["completed", "delivered", "ready", "open", "active"].includes(s)) return "success";
    if (["preparing", "assigned"].includes(s)) return "warning";
    if (["ordered", "incoming"].includes(s)) return "primary";
    if (["out_for_delivery", "on the way", "picked_up"].includes(s)) return "info";
    return "danger";
  };

  // -------------------------------------------------------------
  // RENDER: SUPER ADMIN DASHBOARD
  // -------------------------------------------------------------
  const renderSuperAdminDashboard = () => {
    const kpis = dashData?.kpis || {};
    const activity = dashData?.activity || {};
    const liveFeed = dashData?.liveFeed || [];
    const storePerformance = dashData?.storePerformance || [];
    const issues = dashData?.issues || [];

    return (
      <React.Fragment>
        {/* Row 1: Top KPIs */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg" style={{ background: "linear-gradient(135deg, #fff, #fdf8f6)" }}>
              <CardBody className="d-flex align-items-center">
                <div className="me-3" style={{ backgroundColor: "#fdf0eb", color: "#f4632a", display: "flex", alignItems: "center", justifyContent: "center", width: "48px", height: "48px", minWidth: "48px", minHeight: "48px", borderRadius: "50%", flexShrink: 0 }}>
                  <i className="mdi mdi-cash-check font-size-22" style={{ color: "#f4632a" }}></i>
                </div>
                <div>
                  <h6 className="text-muted text-uppercase font-size-12 mb-1">Today's Sales</h6>
                  <h3 className="mb-0 font-weight-bold" style={{ color: "#111827" }}>{formatCurrency(kpis.todaySales)}</h3>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg">
              <CardBody className="d-flex align-items-center">
                <div className="me-3" style={{ backgroundColor: "#eef2ff", color: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center", width: "48px", height: "48px", minWidth: "48px", minHeight: "48px", borderRadius: "50%", flexShrink: 0 }}>
                  <i className="mdi mdi-receipt font-size-22"></i>
                </div>
                <div>
                  <h6 className="text-muted text-uppercase font-size-12 mb-1">Today's Orders</h6>
                  <h3 className="mb-0 font-weight-bold">{kpis.todayOrders || 0}</h3>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg">
              <CardBody className="d-flex align-items-center">
                <div className="me-3" style={{ backgroundColor: "#ecfdf5", color: "#059669", display: "flex", alignItems: "center", justifyContent: "center", width: "48px", height: "48px", minWidth: "48px", minHeight: "48px", borderRadius: "50%", flexShrink: 0 }}>
                  <i className="mdi mdi-progress-clock font-size-22"></i>
                </div>
                <div>
                  <h6 className="text-muted text-uppercase font-size-12 mb-1">Active Orders</h6>
                  <h3 className="mb-0 font-weight-bold">{kpis.activeOrders || 0}</h3>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg">
              <CardBody className="d-flex align-items-center">
                <div className="me-3" style={{ backgroundColor: "#fef3c7", color: "#d97706", display: "flex", alignItems: "center", justifyContent: "center", width: "48px", height: "48px", minWidth: "48px", minHeight: "48px", borderRadius: "50%", flexShrink: 0 }}>
                  <i className="mdi mdi-store-24-hour font-size-22"></i>
                </div>
                <div>
                  <h6 className="text-muted text-uppercase font-size-12 mb-1">Active Stores</h6>
                  <h3 className="mb-0 font-weight-bold">{kpis.activeStoresText || "0 / 0"}</h3>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Row 2: Secondary KPIs */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg bg-light-subtle">
              <CardBody className="p-3">
                <div className="text-muted font-size-12 text-uppercase mb-1">Delivery Riders Online</div>
                <h4 className="mb-0 font-weight-bold text-dark">{kpis.ridersOnline || 0}</h4>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg bg-light-subtle">
              <CardBody className="p-3">
                <div className="text-muted font-size-12 text-uppercase mb-1">Customers Today</div>
                <h4 className="mb-0 font-weight-bold text-dark">{kpis.customersToday || 0}</h4>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg bg-light-subtle">
              <CardBody className="p-3">
                <div className="text-muted font-size-12 text-uppercase mb-1">Menu Products</div>
                <h4 className="mb-0 font-weight-bold text-dark">{kpis.menuProducts || 0}</h4>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg bg-light-subtle">
              <CardBody className="p-3">
                <div className="text-muted font-size-12 text-uppercase mb-1">Today's Revenue Growth</div>
                <h4 className={classnames("mb-0 font-weight-bold", {
                  "text-success": !kpis.revenueGrowth?.startsWith("-"),
                  "text-danger": kpis.revenueGrowth?.startsWith("-")
                })}>
                  {kpis.revenueGrowth || "0%"}
                </h4>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Clickable Order Activity Row */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-3">
            <h5 className="font-size-14 font-weight-bold mb-3 text-dark">Order Activity</h5>
            <Row className="g-2 text-center">
              <Col xs={6} md={3} lg={1.7} className="col-lg-custom" style={{ cursor: "pointer" }} onClick={() => handleActivityClick("ordered")}>
                <div className="bg-light rounded p-2 hover-shadow">
                  <div className="text-muted font-size-11 mb-1 text-uppercase">Incoming</div>
                  <h4 className="mb-0 font-weight-bold text-primary">{activity.incoming || 0}</h4>
                </div>
              </Col>
              <Col xs={6} md={3} lg={1.7} className="col-lg-custom" style={{ cursor: "pointer" }} onClick={() => handleActivityClick("preparing")}>
                <div className="bg-light rounded p-2 hover-shadow">
                  <div className="text-muted font-size-11 mb-1 text-uppercase">Preparing</div>
                  <h4 className="mb-0 font-weight-bold text-warning">{activity.preparing || 0}</h4>
                </div>
              </Col>
              <Col xs={6} md={3} lg={1.7} className="col-lg-custom" style={{ cursor: "pointer" }} onClick={() => handleActivityClick("ready")}>
                <div className="bg-light rounded p-2 hover-shadow">
                  <div className="text-muted font-size-11 mb-1 text-uppercase">Ready</div>
                  <h4 className="mb-0 font-weight-bold text-success">{activity.ready || 0}</h4>
                </div>
              </Col>
              <Col xs={6} md={3} lg={1.7} className="col-lg-custom" style={{ cursor: "pointer" }} onClick={() => handleActivityClick("out_for_delivery")}>
                <div className="bg-light rounded p-2 hover-shadow">
                  <div className="text-muted font-size-11 mb-1 text-uppercase">Out for Delivery</div>
                  <h4 className="mb-0 font-weight-bold text-info">{activity.outForDelivery || 0}</h4>
                </div>
              </Col>
              <Col xs={6} md={3} lg={1.7} className="col-lg-custom" style={{ cursor: "pointer" }} onClick={() => handleActivityClick("delivered")}>
                <div className="bg-light rounded p-2 hover-shadow">
                  <div className="text-muted font-size-11 mb-1 text-uppercase">Completed Today</div>
                  <h4 className="mb-0 font-weight-bold text-success">{activity.completed || 0}</h4>
                </div>
              </Col>
              <Col xs={6} md={3} lg={1.7} className="col-lg-custom" style={{ cursor: "pointer" }} onClick={() => handleActivityClick("cancelled")}>
                <div className="bg-light rounded p-2 hover-shadow">
                  <div className="text-muted font-size-11 mb-1 text-uppercase">Cancelled</div>
                  <h4 className="mb-0 font-weight-bold text-danger">{activity.cancelled || 0}</h4>
                </div>
              </Col>
              <Col xs={6} md={3} lg={1.7} className="col-lg-custom" style={{ cursor: "pointer" }} onClick={() => handleActivityClick("rejected")}>
                <div className="bg-light rounded p-2 hover-shadow">
                  <div className="text-muted font-size-11 mb-1 text-uppercase">Rejected</div>
                  <h4 className="mb-0 font-weight-bold text-dark">{activity.rejected || 0}</h4>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Main Grid: Left (Chart + Store Table) & Right (Live Feed + Issues + Actions) */}
        <Row>
          <Col lg={8}>
            {/* Sales Hourly Graph */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody>
                <h5 className="font-size-15 font-weight-bold mb-4 text-dark">Sales Trend (Today per Hour)</h5>
                <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={320} />
              </CardBody>
            </Card>

            {/* Store Performance */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody>
                <h5 className="font-size-15 font-weight-bold mb-3 text-dark">Store Performance (Today's Sales)</h5>
                <div className="table-responsive">
                  <Table className="table align-middle table-nowrap mb-0 table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Store Outlet</th>
                        <th className="text-end">Sales Today</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storePerformance.length === 0 ? (
                        <tr>
                          <td colSpan={2} className="text-center py-3 text-muted">No sales logged today yet.</td>
                        </tr>
                      ) : (
                        storePerformance.map((store, idx) => (
                          <tr key={idx}>
                            <td className="font-weight-bold text-dark">{store.name}</td>
                            <td className="text-end text-success font-weight-bold">{formatCurrency(store.sales)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Live Order Feed */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody className="p-3">
                <h5 className="font-size-15 font-weight-bold mb-3 text-dark">
                  Live Order Feed <span className="badge bg-danger rounded-pill float-end font-size-10">Live</span>
                </h5>
                <div style={{ maxHeight: "360px", overflowY: "auto" }} className="pe-1">
                  {liveFeed.length === 0 ? (
                    <div className="text-center py-4 text-muted small">No recent orders registered in live feed.</div>
                  ) : (
                    liveFeed.map((order, idx) => (
                      <div key={order.id || idx}>
                        <div className="d-flex justify-content-between align-items-center py-2">
                          <div>
                            <div className="text-muted font-size-11">{order.time}</div>
                            <div className="font-size-13 font-weight-bold text-dark" style={{ cursor: "pointer" }} onClick={() => navigate("/Orders")}>
                              {order.orderID}
                            </div>
                            <div className="text-muted small">{order.storeName}</div>
                          </div>
                          <div>
                            <Badge color={getStatusColor(order.status)} pill style={{ padding: "4px 10px" }}>
                              {order.status}
                            </Badge>
                          </div>
                        </div>
                        {idx < liveFeed.length - 1 && <hr className="my-1 border-light" />}
                      </div>
                    ))
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Recent Issues Panel */}
            <Card className="border-0 shadow-sm mb-4 bg-soft-danger" style={{ backgroundColor: "#fef2f2", border: "1px solid #fee2e2" }}>
              <CardBody className="p-3">
                <h5 className="font-size-14 font-weight-bold text-danger mb-3">
                  <i className="mdi mdi-alert-circle me-1"></i> Recent Operational Issues
                </h5>
                <div style={{ fontSize: 13, color: "#991b1b" }}>
                  {issues.length === 0 ? (
                    <div className="text-success small py-1 font-weight-bold">🟢 All systems operational. No warnings active.</div>
                  ) : (
                    issues.map((iss, idx) => (
                      <div key={idx} className="mb-2 d-flex align-items-start gap-2">
                        <span className="mdi mdi-alert-outline text-danger font-size-16" style={{ marginTop: "-2px" }}></span>
                        <div>{iss.message}</div>
                      </div>
                    ))
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Quick Actions Panel */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody className="p-3">
                <h5 className="font-size-14 font-weight-bold mb-3 text-dark">Quick Administration Actions</h5>
                <div className="d-grid gap-2">
                  <Button color="light" className="text-start border shadow-none" onClick={() => navigate("/stores")}>
                    🚀 <strong className="ms-1 text-dark">+ New Store Outlet</strong>
                  </Button>
                  <Button color="light" className="text-start border shadow-none" onClick={() => navigate("/promotions")}>
                    🎫 <strong className="ms-1 text-dark">+ New Promotion / Coupon</strong>
                  </Button>
                  <Button color="light" className="text-start border shadow-none" onClick={() => navigate("/Delivery")}>
                    🛵 <strong className="ms-1 text-dark">+ Add Delivery Rider</strong>
                  </Button>
                  <Button color="light" className="text-start border shadow-none" onClick={() => navigate("/catalog/products")}>
                    🍕 <strong className="ms-1 text-dark">+ Add Product Item</strong>
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  // -------------------------------------------------------------
  // RENDER: STORE ADMIN DASHBOARD
  // -------------------------------------------------------------
  const renderStoreAdminDashboard = () => {
    const topCards = dashData?.topCards || {};
    const secondRow = dashData?.secondRow || {};
    const kitchenQueue = dashData?.kitchenQueue || [];
    const deliveryQueue = dashData?.deliveryQueue || {};
    const timing = dashData?.timing || {};
    const summary = dashData?.summary || {};

    const hasLiveOrders = liveOrders.length > 0;

    return (
      <React.Fragment>
        {/* Attention pulse for the live-orders badge/dot when there's
            something to act on — pure CSS, injected once with the widget. */}
        <style>{`
          @keyframes livePulse {
            0%   { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5); }
            70%  { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
            100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
          }
        `}</style>

        {/* Live Orders leads the dashboard — it's the store's "what needs my
            action right now" list, so it sits above the status/metric cards
            and is visually emphasized (accent top border, icon, live count,
            pulsing dot) so a new order can't be missed. */}
        <Card
          className="border-0 shadow-sm mb-4"
          style={{ borderTop: `4px solid ${hasLiveOrders ? "#f4632a" : "#e5e7eb"}` }}
        >
          <CardBody className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center">
                <div
                  className="me-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    minWidth: 44,
                    borderRadius: "50%",
                    backgroundColor: hasLiveOrders ? "#fdf0eb" : "#f3f4f6",
                  }}
                >
                  <i
                    className="mdi mdi-bell-ring font-size-22"
                    style={{ color: hasLiveOrders ? "#f4632a" : "#9ca3af" }}
                  ></i>
                </div>
                <div>
                  <h4 className="font-size-18 font-weight-bold mb-0 text-dark d-flex align-items-center">
                    Live Orders
                    {hasLiveOrders && (
                      <span
                        className="ms-2 d-inline-flex align-items-center justify-content-center text-white font-weight-bold"
                        style={{
                          minWidth: 26,
                          height: 26,
                          padding: "0 8px",
                          borderRadius: 13,
                          backgroundColor: "#ef4444",
                          fontSize: 13,
                          animation: "livePulse 1.6s infinite",
                        }}
                      >
                        {liveOrders.length}
                      </span>
                    )}
                  </h4>
                  <small className="text-muted">
                    {hasLiveOrders
                      ? "Orders that need your attention right now"
                      : "All caught up — new orders appear here instantly"}
                  </small>
                </div>
              </div>
              <Button
                color={hasLiveOrders ? "danger" : "light"}
                size="sm"
                className={hasLiveOrders ? "shadow-none" : "border shadow-none"}
                onClick={() => navigate("/Orders/live")}
              >
                View All <i className="mdi mdi-arrow-right"></i>
              </Button>
            </div>
            <div className="table-responsive">
              <Table className="table align-middle table-nowrap mb-0 table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Method</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {!hasLiveOrders ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4 text-muted">
                        ✅ No live orders right now — all caught up.
                      </td>
                    </tr>
                  ) : (
                    liveOrders.slice(0, 8).map((order) => (
                      <tr
                        key={order.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate("/Orders/live")}
                      >
                        <td className="font-weight-bold text-primary">{order.orderID || "N/A"}</td>
                        <td>{order.customerName || "N/A"}</td>
                        <td className="text-capitalize">{order.method}</td>
                        <td>
                          <Badge color={getStatusColor(order.status)} style={{ padding: "5px 12px" }}>
                            {order.status}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* Row 1: Top Status Cards (Incoming, Preparing, Ready, Out for Delivery) */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg" style={{ borderLeft: "4px solid #3b82f6" }}>
              <CardBody className="p-3">
                <div className="text-muted font-size-12 text-uppercase mb-1">Incoming Orders</div>
                <h2 className="mb-0 font-weight-bold text-primary">{topCards.incoming || 0}</h2>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg" style={{ borderLeft: "4px solid #f59e0b" }}>
              <CardBody className="p-3">
                <div className="text-muted font-size-12 text-uppercase mb-1">Preparing</div>
                <h2 className="mb-0 font-weight-bold text-warning">{topCards.preparing || 0}</h2>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg" style={{ borderLeft: "4px solid #10b981" }}>
              <CardBody className="p-3">
                <div className="text-muted font-size-12 text-uppercase mb-1">Ready to Deliver</div>
                <h2 className="mb-0 font-weight-bold text-success">{topCards.ready || 0}</h2>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm rounded-lg" style={{ borderLeft: "4px solid #06b6d4" }}>
              <CardBody className="p-3">
                <div className="text-muted font-size-12 text-uppercase mb-1">Out for Delivery</div>
                <h2 className="mb-0 font-weight-bold text-info">{topCards.outForDelivery || 0}</h2>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Row 2: Store Metrics Row */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-3">
                <div className="text-muted font-size-12 mb-1">Today's Sales</div>
                <h3 className="mb-0 font-weight-bold text-success">{formatCurrency(secondRow.todaySales)}</h3>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-3">
                <div className="text-muted font-size-12 mb-1">Today's Orders</div>
                <h3 className="mb-0 font-weight-bold text-dark">{secondRow.todayOrders || 0}</h3>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-3">
                <div className="text-muted font-size-12 mb-1">Riders Available</div>
                <h3 className="mb-0 font-weight-bold text-info">{secondRow.ridersAvailable || 0}</h3>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-3">
                <div className="text-muted font-size-12 mb-1">Pending Deliveries</div>
                <h3 className="mb-0 font-weight-bold text-warning">{secondRow.pendingDeliveries || 0}</h3>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Store Dashboard Main layout */}
        <Row>
          <Col lg={8}>
            {/* Kitchen Queue */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody>
                <h5 className="font-size-15 font-weight-bold mb-3 text-dark">Kitchen Queue</h5>
                <div className="table-responsive">
                  <Table className="table align-middle table-nowrap mb-0 table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Order ID</th>
                        <th>Product Items</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kitchenQueue.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-center py-4 text-muted">
                            🍳 Kitchen queue is empty. No active preparation needed.
                          </td>
                        </tr>
                      ) : (
                        kitchenQueue.map((item, idx) => (
                          <tr key={item.id || idx}>
                            <td className="font-weight-bold text-primary" style={{ cursor: "pointer" }} onClick={() => navigate("/Orders")}>
                              {item.orderID}
                            </td>
                            <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{item.itemsSummary}</td>
                            <td>
                              <Badge color={getStatusColor(item.status)} style={{ padding: "5px 12px" }}>
                                {item.status}
                              </Badge>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>

            {/* Delivery Queue Details */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody className="p-3">
                <h5 className="font-size-15 font-weight-bold mb-3 text-dark">Delivery Queue</h5>
                <Row className="text-center g-2">
                  <Col md={3}>
                    <div className="bg-light p-3 rounded">
                      <div className="text-muted font-size-12 mb-1">Ready to Assign</div>
                      <h3 className="mb-0 font-weight-bold text-warning">{deliveryQueue.readyToAssign || 0}</h3>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="bg-light p-3 rounded">
                      <div className="text-muted font-size-12 mb-1">Assigned</div>
                      <h3 className="mb-0 font-weight-bold text-primary">{deliveryQueue.assigned || 0}</h3>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="bg-light p-3 rounded">
                      <div className="text-muted font-size-12 mb-1">Picked Up</div>
                      <h3 className="mb-0 font-weight-bold text-info">{deliveryQueue.pickedUp || 0}</h3>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="bg-light p-3 rounded">
                      <div className="text-muted font-size-12 mb-1">Failed / Retrying</div>
                      <h3 className="mb-0 font-weight-bold text-danger">{deliveryQueue.failed || 0}</h3>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Store Timing Status */}
            <Card className="border-0 shadow-sm mb-4 text-center">
              <CardBody className="p-3">
                <h5 className="font-size-14 font-weight-bold mb-3 text-dark text-start">Store Timing</h5>
                <div className="d-flex align-items-center justify-content-between border rounded p-3 bg-light">
                  <div className="text-start">
                    <span className="text-muted small d-block">Operating Hours</span>
                    <strong>{timing.hoursText || "10 AM - 11 PM"}</strong>
                  </div>
                  <Badge color={timing.statusText === "OPEN" ? "success" : "danger"} style={{ fontSize: "16px", padding: "6px 16px" }} pill>
                    {timing.statusText || "CLOSED"}
                  </Badge>
                </div>
              </CardBody>
            </Card>

            {/* Low Stock Alert (Future) — hidden until backed by real
                inventory data. The Paneer/Cheese rows below were hardcoded
                placeholders, not live stock, so they were misleading on a
                real store dashboard. Restore this block (and wire it to a
                real low-stock source) when the feature is actually built.
            <Card className="border-0 shadow-sm mb-4">
              <CardBody className="p-3">
                <h5 className="font-size-14 font-weight-bold text-danger mb-3">
                  <i className="mdi mdi-alert-circle-outline me-1"></i> Low Stock Alert (Future)
                </h5>
                <div style={{ fontSize: 13, color: "#374151" }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="font-weight-bold">Paneer</span>
                    <Badge color="warning" pill>Only 3 left</Badge>
                  </div>
                  <hr className="my-1 border-light" />
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <span className="font-weight-bold">Cheese</span>
                    <Badge color="danger" pill>Out of Stock</Badge>
                  </div>
                </div>
              </CardBody>
            </Card>
            */}

            {/* Today's Summary Dashboard Card */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody className="p-3">
                <h5 className="font-size-15 font-weight-bold mb-3 text-dark">Today's Summary</h5>
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                    <span className="text-muted">Total Orders</span>
                    <strong className="font-size-16 text-dark">{summary.orders || 0}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                    <span className="text-muted">Revenue Generated</span>
                    <strong className="font-size-16 text-success">{formatCurrency(summary.revenue)}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">Average Order Value</span>
                    <strong className="font-size-16 text-primary">{formatCurrency(summary.averageOrder)}</strong>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          {/* Custom Header Bar */}
          <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
            <div>
              <h3 className="mb-0 text-dark font-weight-bold" style={{ fontSize: "24px", letterSpacing: "-0.02em" }}>
                {role === "admin" ? `${dashData?.storeName || "Store"} Dashboard` : "Business Metrics & Analytics"}
              </h3>
              <div className="text-muted small mt-1 font-size-12">
                susipizza <span className="mx-2">&gt;</span> <strong>Dashboard</strong>
              </div>
            </div>
            <div>
              <Button
                color="primary"
                className="border-0 shadow-sm px-4 py-2"
                style={{
                  borderRadius: "8px",
                  backgroundColor: "#f4632a",
                  fontWeight: "600",
                }}
                onClick={handleRefresh}
              >
                <i className="mdi mdi-refresh me-2"></i>Refresh
              </Button>
            </div>
          </div>

          {role === "admin" ? renderStoreAdminDashboard() : renderSuperAdminDashboard()}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
